import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { GeoJSON, MapContainer, Marker, Rectangle, TileLayer, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import authFetch from '../utils/authFetch';

const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocal ? 'http://localhost:5000' : 'https://bdinteldb.com';
const CLICK_WINDOW_MS = 2000;
const SELECT_CLICKS = 3;
const DESELECT_CLICKS = 4;
const CELL_SIZE_KM = 1;
const KM_PER_LAT_DEGREE = 111.32;
const SLOVENIA_BOUNDS = [
  [45.42, 13.38],
  [46.88, 16.61]
];

const buildGridCells = () => {
  const [[minLat, minLng], [maxLat, maxLng]] = SLOVENIA_BOUNDS;
  const latStep = CELL_SIZE_KM / KM_PER_LAT_DEGREE;
  const cells = [];

  for (let lat = minLat; lat < maxLat; lat += latStep) {
    const nextLat = Math.min(lat + latStep, maxLat);
    const centerLat = lat + ((nextLat - lat) / 2);
    const lonStep = CELL_SIZE_KM / (KM_PER_LAT_DEGREE * Math.cos((centerLat * Math.PI) / 180));

    for (let lng = minLng; lng < maxLng; lng += lonStep) {
      const nextLng = Math.min(lng + lonStep, maxLng);
      cells.push({
        id: `${lat.toFixed(5)}:${lng.toFixed(5)}`,
        bounds: [
          [lat, lng],
          [nextLat, nextLng]
        ]
      });
    }
  }

  return cells;
};

const getFeatureName = (feature) => {
  const properties = feature?.properties || {};
  return properties.shapeName || properties.name || properties.NAME_2 || properties.ADM2_NAME || properties.admin2Name || 'Občina';
};

const collectCoordinatePairs = (coordinates, pairs = []) => {
  if (!Array.isArray(coordinates)) {
    return pairs;
  }

  if (typeof coordinates[0] === 'number' && typeof coordinates[1] === 'number') {
    pairs.push([coordinates[1], coordinates[0]]);
    return pairs;
  }

  coordinates.forEach((entry) => collectCoordinatePairs(entry, pairs));
  return pairs;
};

const getFeatureCenter = (feature) => {
  const pairs = collectCoordinatePairs(feature?.geometry?.coordinates || []);
  if (pairs.length === 0) {
    return null;
  }

  const bounds = pairs.reduce((accumulator, [lat, lng]) => ({
    minLat: Math.min(accumulator.minLat, lat),
    maxLat: Math.max(accumulator.maxLat, lat),
    minLng: Math.min(accumulator.minLng, lng),
    maxLng: Math.max(accumulator.maxLng, lng),
  }), {
    minLat: Number.POSITIVE_INFINITY,
    maxLat: Number.NEGATIVE_INFINITY,
    minLng: Number.POSITIVE_INFINITY,
    maxLng: Number.NEGATIVE_INFINITY,
  });

  return [
    bounds.minLat + ((bounds.maxLat - bounds.minLat) / 2),
    bounds.minLng + ((bounds.maxLng - bounds.minLng) / 2),
  ];
};

const municipalityLabelIcon = (name) => L.divIcon({
  className: '',
  html: `<div style="background:rgba(255,255,255,0.72);border:1px solid rgba(148,163,184,0.6);border-radius:6px;padding:2px 6px;color:#0f172a;font-size:10px;font-weight:600;white-space:nowrap;pointer-events:none;">${name}</div>`,
  iconSize: null,
});

const boundsIntersect = (cellBounds, visibleBounds) => {
  if (!visibleBounds) {
    return true;
  }

  const [[cellMinLat, cellMinLng], [cellMaxLat, cellMaxLng]] = cellBounds;
  const southWest = visibleBounds.getSouthWest();
  const northEast = visibleBounds.getNorthEast();

  return !(
    cellMaxLat < southWest.lat
    || cellMinLat > northEast.lat
    || cellMaxLng < southWest.lng
    || cellMinLng > northEast.lng
  );
};

const pointInsideBounds = (point, visibleBounds) => {
  if (!visibleBounds || !point) {
    return true;
  }

  const [lat, lng] = point;
  const southWest = visibleBounds.getSouthWest();
  const northEast = visibleBounds.getNorthEast();

  return lat >= southWest.lat && lat <= northEast.lat && lng >= southWest.lng && lng <= northEast.lng;
};

const MapViewportTracker = ({ onViewportChange }) => {
  useMapEvents({
    moveend: (event) => {
      onViewportChange(event.target.getBounds().pad(0.2), event.target.getZoom());
    },
    zoomend: (event) => {
      onViewportChange(event.target.getBounds().pad(0.2), event.target.getZoom());
    }
  });

  return null;
};

const CheckedAreasMapPage = () => {
  const navigate = useNavigate();
  const [selectedCellIds, setSelectedCellIds] = useState(new Set());
  const [municipalities, setMunicipalities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showSquareBorders, setShowSquareBorders] = useState(true);
  const [showMunicipalityTitles, setShowMunicipalityTitles] = useState(true);
  const [showMunicipalityBorders, setShowMunicipalityBorders] = useState(true);
  const [visibleBounds, setVisibleBounds] = useState(() => L.latLngBounds(SLOVENIA_BOUNDS).pad(0.2));
  const [mapZoom, setMapZoom] = useState(9);
  const clickStateRef = useRef({});
  const gridCells = useMemo(() => buildGridCells(), []);
  const visibleGridCells = useMemo(
    () => gridCells.filter((cell) => boundsIntersect(cell.bounds, visibleBounds)),
    [gridCells, visibleBounds]
  );
  const visibleMunicipalities = useMemo(
    () => municipalities.filter((feature) => {
      const featureCenter = Number.isFinite(feature?.centerLat) && Number.isFinite(feature?.centerLng)
        ? [feature.centerLat, feature.centerLng]
        : getFeatureCenter(feature);
      return pointInsideBounds(featureCenter, visibleBounds);
    }),
    [municipalities, visibleBounds]
  );

  const toggleButtonStyle = {
    border: '1px solid #cbd5e1',
    borderRadius: '10px',
    backgroundColor: 'rgba(255,255,255,0.96)',
    color: '#0f172a',
    fontSize: '12px',
    fontWeight: 600,
    padding: '8px 10px',
    cursor: 'pointer',
    textAlign: 'left'
  };

  useEffect(() => {
    const loadSelectedCells = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await authFetch(`${API_BASE}/api/leads/checked-areas`);
        if (!response.ok) {
          throw new Error('Failed to load checked areas');
        }

        const data = await response.json();
        setSelectedCellIds(new Set(Array.isArray(data.selectedCellIds) ? data.selectedCellIds.map((entry) => String(entry)) : []));
      } catch (loadError) {
        console.error(loadError);
        setError('Could not load checked areas.');
      } finally {
        setLoading(false);
      }
    };

    loadSelectedCells();
  }, []);

  useEffect(() => {
    const loadMunicipalities = async () => {
      try {
        const response = await authFetch(`${API_BASE}/api/leads/municipality-boundaries`);
        if (!response.ok) {
          throw new Error('Failed to load municipality borders');
        }

        const data = await response.json();
        const features = Array.isArray(data?.features) ? data.features : [];
        setMunicipalities(features);
      } catch (loadBoundaryError) {
        console.error(loadBoundaryError);
        setError((current) => current || 'Could not load municipality borders.');
      }
    };

    loadMunicipalities();
  }, []);

  const handleCellClick = async (cellId) => {
    const now = Date.now();
    const isSelected = selectedCellIds.has(cellId);
    const requiredClicks = isSelected ? DESELECT_CLICKS : SELECT_CLICKS;
    const currentMode = isSelected ? 'selected' : 'unselected';
    const previousState = clickStateRef.current[cellId];

    const nextState = previousState
      && previousState.mode === currentMode
      && (now - previousState.lastClickAt) <= CLICK_WINDOW_MS
      ? { mode: currentMode, count: previousState.count + 1, lastClickAt: now }
      : { mode: currentMode, count: 1, lastClickAt: now };

    if (nextState.count >= requiredClicks) {
      const nextSelected = !isSelected;
      const previousSelectedCellIds = new Set(selectedCellIds);
      setSelectedCellIds((current) => {
        const next = new Set(current);
        if (nextSelected) {
          next.add(cellId);
        } else {
          next.delete(cellId);
        }
        return next;
      });
      delete clickStateRef.current[cellId];

      try {
        const response = await authFetch(`${API_BASE}/api/leads/checked-areas/${encodeURIComponent(cellId)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ selected: nextSelected })
        });

        if (!response.ok) {
          throw new Error('Failed to save checked area');
        }
      } catch (saveError) {
        console.error(saveError);
        setError('Could not save checked area selection.');
        setSelectedCellIds(previousSelectedCellIds);
      }

      return;
    }

    clickStateRef.current[cellId] = nextState;
  };

  return (
    <div style={{ padding: '24px', height: '100vh', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button
          type="button"
          onClick={() => navigate('/real-estate/lead-gen')}
          style={{ padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', backgroundColor: '#f8fafc', cursor: 'pointer' }}
        >
          <ArrowLeft size={16} />
        </button>
        <div>
          <h1 style={{ margin: 0, color: '#0f172a' }}>Checked Areas</h1>
          <p style={{ margin: '4px 0 0', color: '#64748b' }}>Satellite view of Slovenia with 1x1 km squares. Click a square 3 times within 2 seconds to mark it as checked. Click a selected square 4 times to clear it.</p>
        </div>
      </div>

      {error ? (
        <div style={{ color: '#b91c1c', fontSize: '14px' }}>{error}</div>
      ) : null}

      <div style={{ flex: 1, minHeight: 0, borderRadius: '16px', overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: '0 4px 10px rgba(15, 23, 42, 0.06)', position: 'relative' }}>
        <div style={{ position: 'absolute', top: '12px', right: '12px', zIndex: 1000, display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '170px' }}>
          <button type="button" onClick={() => setShowSquareBorders((current) => !current)} style={toggleButtonStyle}>
            {showSquareBorders ? 'Hide square borders' : 'Show square borders'}
          </button>
          <button type="button" onClick={() => setShowMunicipalityTitles((current) => !current)} style={toggleButtonStyle}>
            {showMunicipalityTitles ? 'Hide municipality titles' : 'Show municipality titles'}
          </button>
          <button type="button" onClick={() => setShowMunicipalityBorders((current) => !current)} style={toggleButtonStyle}>
            {showMunicipalityBorders ? 'Hide municipality borders' : 'Show municipality borders'}
          </button>
        </div>
        <MapContainer bounds={SLOVENIA_BOUNDS} scrollWheelZoom doubleClickZoom={false} preferCanvas style={{ height: '100%', width: '100%' }}>
          <MapViewportTracker onViewportChange={(nextBounds, nextZoom) => {
            setVisibleBounds(nextBounds);
            setMapZoom(nextZoom);
          }} />
          <TileLayer
            attribution='Tiles &copy; Esri'
            url='https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
          />
          {showMunicipalityBorders ? visibleMunicipalities.map((feature, index) => {
            const featureCenter = Number.isFinite(feature?.centerLat) && Number.isFinite(feature?.centerLng)
              ? [feature.centerLat, feature.centerLng]
              : getFeatureCenter(feature);
            const featureName = getFeatureName(feature);

            return (
              <React.Fragment key={`${featureName}-${index}`}>
                <GeoJSON
                  data={feature}
                  style={{
                    color: '#38bdf8',
                    weight: 2,
                    opacity: 0.95,
                    fillOpacity: 0,
                    interactive: false,
                  }}
                />
                {showMunicipalityTitles && mapZoom >= 10 && featureCenter ? (
                  <Marker position={featureCenter} icon={municipalityLabelIcon(featureName)} interactive={false} />
                ) : null}
              </React.Fragment>
            );
          }) : (showMunicipalityTitles && mapZoom >= 10 ? visibleMunicipalities.map((feature, index) => {
            const featureCenter = Number.isFinite(feature?.centerLat) && Number.isFinite(feature?.centerLng)
              ? [feature.centerLat, feature.centerLng]
              : getFeatureCenter(feature);
            const featureName = getFeatureName(feature);

            return featureCenter ? (
              <Marker key={`${featureName}-${index}`} position={featureCenter} icon={municipalityLabelIcon(featureName)} interactive={false} />
            ) : null;
          }) : null)}
          {visibleGridCells.map((cell) => {
            const isSelected = selectedCellIds.has(cell.id);

            return (
              <Rectangle
                key={cell.id}
                bounds={cell.bounds}
                pathOptions={{
                  color: '#ffffff',
                  weight: showSquareBorders ? (isSelected ? 1.4 : 1) : 0,
                  fillColor: '#dc2626',
                  fillOpacity: isSelected ? 0.28 : 0,
                  opacity: showSquareBorders ? (isSelected ? 0.95 : 0.55) : 0
                }}
                eventHandlers={{
                  click: () => {
                    if (!loading) {
                      handleCellClick(cell.id);
                    }
                  }
                }}
              />
            );
          })}
        </MapContainer>
      </div>
    </div>
  );
};

export default CheckedAreasMapPage;